import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  ReferenceLine, ResponsiveContainer,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Users, X } from "lucide-react";

/* Per-metric colour identities — same palette as the main DistributionChart. */
export const METRICS = [
  { key: "overall",   label: "Overall",   stroke: "#10B981" },
  { key: "finishing", label: "Finishing", stroke: "#3B82F6" },
  { key: "passing",   label: "Passing",   stroke: "#EAB308" },
  { key: "defending", label: "Defending", stroke: "#EF4444" },
];

export function lastWord(s) {
  if (!s) return "";
  const parts = String(s).trim().split(/\s+/);
  return parts[parts.length - 1] || s;
}

export function MetricChart({ metric, current, comparison }) {
  // Build the row-per-x dataset
  const data = useMemo(() => {
    if (!current) return [];
    const xs = current.x;
    return xs.map((x, i) => ({
      x,
      A: current[metric.key][i],
      B: comparison ? comparison[metric.key][i] : null,
    }));
  }, [current, comparison, metric.key]);

  if (!current) return null;
  const aValue = current.values?.[metric.key];
  const bValue = comparison?.values?.[metric.key];
  const aName = current._name || "Player A";
  const bName = comparison?._name || "Player B";
  const gradAId = `cmp-grad-A-${metric.key}`;
  const gradBId = `cmp-grad-B-${metric.key}`;

  return (
    <div className="rounded-xl border border-border bg-card p-4 flex flex-col" data-testid={`compare-chart-${metric.key}`}>
      <div className="flex items-baseline justify-between gap-2 mb-2 px-1">
        <h3 className="font-display uppercase tracking-[0.16em] text-xs" style={{ color: metric.stroke }}>
          {metric.label}
        </h3>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          0–100 %
        </div>
      </div>

      <div style={{ width: "100%", height: 240, minHeight: 240 }} data-testid={`compare-chart-canvas-${metric.key}`}>
        <ResponsiveContainer width="100%" height="100%" minWidth={240} minHeight={200}>
          <AreaChart data={data} margin={{ top: 18, right: 16, left: 0, bottom: 18 }}>
            <defs>
              <linearGradient id={gradAId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor={metric.stroke} stopOpacity={0.5} />
                <stop offset="55%" stopColor={metric.stroke} stopOpacity={0.18} />
                <stop offset="100%" stopColor={metric.stroke} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id={gradBId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor={metric.stroke} stopOpacity={0.25} />
                <stop offset="55%" stopColor={metric.stroke} stopOpacity={0.10} />
                <stop offset="100%" stopColor={metric.stroke} stopOpacity={0.02} />
              </linearGradient>
            </defs>

            <CartesianGrid stroke="currentColor" strokeOpacity={0.06} vertical={false} />

            <XAxis
              dataKey="x" type="number" domain={[0, 100]}
              ticks={[0, 20, 40, 60, 80, 100]}
              tick={{ fontSize: 10, fill: "currentColor", opacity: 0.55 }}
              tickLine={false}
              axisLine={{ stroke: "currentColor", strokeOpacity: 0.15 }}
            />
            <YAxis hide />

            <Area
              type="monotone" dataKey="A" name={aName}
              stroke={metric.stroke} strokeWidth={2.5}
              fill={`url(#${gradAId})`}
              isAnimationActive animationDuration={500}
              dot={false} activeDot={false}
            />
            {comparison && (
              <Area
                type="monotone" dataKey="B" name={bName}
                stroke={metric.stroke} strokeWidth={2.5}
                strokeDasharray="6 4"
                fill={`url(#${gradBId})`}
                isAnimationActive animationDuration={500}
                dot={false} activeDot={false}
              />
            )}

            {aValue != null && (
              <ReferenceLine
                x={Math.round(aValue)}
                stroke={metric.stroke}
                strokeDasharray="2 4"
                strokeOpacity={0.85}
                ifOverflow="extendDomain"
                label={{
                  value: `${lastWord(aName)} ${aValue.toFixed(1)}`,
                  position: "insideTopLeft",
                  fill: metric.stroke,
                  fontSize: 10, offset: 6, dy: 6, dx: 4,
                  fontFamily: "var(--font-display, inherit)",
                }}
              />
            )}
            {comparison && bValue != null && (
              <ReferenceLine
                x={Math.round(bValue)}
                stroke={metric.stroke}
                strokeDasharray="2 4"
                strokeOpacity={0.55}
                ifOverflow="extendDomain"
                label={{
                  value: `${lastWord(bName)} ${bValue.toFixed(1)}`,
                  position: "insideTopLeft",
                  fill: metric.stroke,
                  fontSize: 10, offset: 6, dy: 20, dx: 4,
                  fontFamily: "var(--font-display, inherit)",
                }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Per-chart legend */}
      <div className="flex flex-wrap items-center justify-center gap-3 mt-2 px-1" data-testid={`compare-legend-${metric.key}`}>
        <span className="inline-flex items-center gap-1.5 text-[11px]">
          <span className="inline-block h-[2px] w-5" style={{ background: metric.stroke }} />
          <span className="text-foreground/85">{aName}</span>
          <span className="font-mono text-foreground/60">{aValue != null ? aValue.toFixed(1) : "—"}</span>
        </span>
        {comparison && (
          <span className="inline-flex items-center gap-1.5 text-[11px]">
            <span
              className="inline-block w-5"
              style={{
                borderTop: `2px dashed ${metric.stroke}`,
                opacity: 0.75,
              }}
            />
            <span className="text-foreground/85">{bName}</span>
            <span className="font-mono text-foreground/60">{bValue != null ? bValue.toFixed(1) : "—"}</span>
          </span>
        )}
      </div>
    </div>
  );
}

export default function PlayerComparison({ playerId, currentPlayerName, focusOverride, focusOverrideLabel }) {
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);

  const [leagueId, setLeagueId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [otherId, setOtherId] = useState("");

  const [current, setCurrent] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loadingCmp, setLoadingCmp] = useState(false);

  // Phase 9: independent range selector for the comparison section.
  // Default "career". When changed, BOTH current and comparison refetch
  // with the same range. State is independent from the DistributionChart's range.
  const [range, setRange] = useState("career");

  const overrideActive = !!focusOverride;
  const overrideKey = focusOverride ? JSON.stringify(focusOverride) : "";

  // Load static lists once
  useEffect(() => {
    let alive = true;
    Promise.all([api.get("/leagues"), api.get("/teams")])
      .then(([l, t]) => { if (alive) { setLeagues(l.data); setTeams(t.data); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Always fetch current player's distribution (with current range);
  // refetch when ignore_friendlies or range changes too.
  useEffect(() => {
    if (overrideActive) return;  // Phase 12 — override path takes over
    let alive = true;
    setCurrent(null);
    api.get(`/players/${playerId}/distribution`, { params: { range } })
      .then((r) => { if (alive) setCurrent({ ...r.data, _name: currentPlayerName || "This player" }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [playerId, currentPlayerName, ignoreFriendlies, range, overrideActive]);

  // Phase 12 — when a focus override is active, replace the focus player's
  // curves with the simulated distribution from /distribution-from-values.
  useEffect(() => {
    if (!overrideActive) return;
    let alive = true;
    api.post("/distribution-from-values", focusOverride)
      .then((r) => {
        if (!alive) return;
        const simName = focusOverrideLabel
          ? `${currentPlayerName || "This player"} · ${focusOverrideLabel}`
          : `${currentPlayerName || "This player"} (Transfer simulation)`;
        setCurrent({ ...r.data, _name: simName, available_ranges: current?.available_ranges || [] });
      })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrideActive, overrideKey, focusOverrideLabel, currentPlayerName]);

  // When team changes, fetch players in that team (server-side filter)
  useEffect(() => {
    if (!teamId) { setPlayers([]); setOtherId(""); return; }
    let alive = true;
    api.get("/players", { params: { team_id: teamId } })
      .then((r) => {
        if (alive) setPlayers((r.data || []).filter((p) => p.id !== playerId));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [teamId, playerId]);

  // When comparison player picked, fetch their distribution at the same range;
  // refetch when range or ignore_friendlies changes.
  useEffect(() => {
    if (!otherId) { setComparison(null); return; }
    let alive = true;
    setLoadingCmp(true);
    const meta = players.find((p) => p.id === otherId);
    api.get(`/players/${otherId}/distribution`, { params: { range } })
      .then((r) => { if (alive) setComparison({ ...r.data, _name: meta?.full_name || "Comparison" }); })
      .catch(() => { if (alive) setComparison(null); })
      .finally(() => { if (alive) setLoadingCmp(false); });
    return () => { alive = false; };
  }, [otherId, players, ignoreFriendlies, range]);

  const teamsForLeague = leagueId
    ? teams.filter((t) => (t.league_ids || []).includes(leagueId))
    : [];

  const clearComparison = () => {
    setLeagueId(""); setTeamId(""); setOtherId(""); setComparison(null);
  };

  // The focus player's available ranges drive the dropdown.
  const ranges = current?.available_ranges || [];

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="player-comparison">
      <header className="px-6 pt-6 pb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Head to head</div>
          <h2 className="font-display uppercase tracking-tight text-xl" data-testid="comparison-card-title">
            {comparison?._name && currentPlayerName
              ? `Comparing ${currentPlayerName}${overrideActive ? " (Transfer simulation)" : ""} with ${comparison._name}`
              : "Compare with another player"}
          </h2>
        </div>
        <div className="flex items-end gap-3">
          {comparison && ranges.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Range{loadingCmp ? " · loading…" : ""}
              </span>
              <Select value={range} onValueChange={setRange}>
                <SelectTrigger className="h-9 min-w-[180px]" data-testid="comparison-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ranges.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      data-testid={`comparison-range-option-${r.value.replace("/", "-")}`}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {comparison && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearComparison}
              data-testid="comparison-clear"
              className="h-9 gap-1.5"
            >
              <X className="h-4 w-4" /> Clear comparison
            </Button>
          )}
        </div>
      </header>

      {/* Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 px-6 pt-3 pb-4">
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">League</Label>
          <Select value={leagueId || "__none__"} onValueChange={(v) => { setLeagueId(v === "__none__" ? "" : v); setTeamId(""); setOtherId(""); }}>
            <SelectTrigger data-testid="comparison-league-select">
              <SelectValue placeholder="Pick a league" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Pick a league…</SelectItem>
              {leagues.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`comparison-league-option-${l.id}`}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Team</Label>
          <Select value={teamId || "__none__"} onValueChange={(v) => { setTeamId(v === "__none__" ? "" : v); setOtherId(""); }} disabled={!leagueId}>
            <SelectTrigger data-testid="comparison-team-select">
              <SelectValue placeholder={leagueId ? "Pick a team" : "Pick league first"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{leagueId ? "Pick a team…" : "Pick league first"}</SelectItem>
              {teamsForLeague.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`comparison-team-option-${t.id}`}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Player</Label>
          <Select value={otherId || "__none__"} onValueChange={(v) => setOtherId(v === "__none__" ? "" : v)} disabled={!teamId}>
            <SelectTrigger data-testid="comparison-player-select">
              <SelectValue placeholder={teamId ? (players.length ? "Pick a player" : "No other players") : "Pick team first"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{teamId ? (players.length ? "Pick a player…" : "No other players in this team") : "Pick team first"}</SelectItem>
              {players.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`comparison-player-option-${p.id}`}>{p.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 pb-6">
        {!comparison ? (
          <div
            className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center"
            data-testid="comparison-empty"
          >
            <Users className="h-7 w-7 mx-auto mb-3 text-muted-foreground" />
            <div className="font-display uppercase tracking-[0.18em] text-xs text-muted-foreground">
              {loadingCmp ? "Loading comparison…" : "Pick a player to compare"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Choose a league, then a team, then a player — their behavioural curves will overlay {currentPlayerName ? <span className="text-foreground/80">{currentPlayerName}'s</span> : "this player's"} curves below.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="comparison-grid">
            {METRICS.map((m) => (
              <MetricChart key={m.key} metric={m} current={current} comparison={comparison} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
